import { useEffect, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiRequest } from '../api/client'

interface TosCurrent {
  id: string
  version: number
  title: string
  content: string
  published_at: string | null
}

interface TosHistoryRow {
  version: number
  title: string
  is_published: boolean
  published_at: string | null
  updated_by: string | null
  acceptances: number
}

interface AdminTosResponse {
  current: TosCurrent | null
  history: TosHistoryRow[]
}

export default function TermsOfService() {
  const queryClient = useQueryClient()
  const { data, isLoading, error } = useQuery<AdminTosResponse>({
    queryKey: ['admin-tos'],
    queryFn: () => apiRequest<AdminTosResponse>('/api/admin/tos'),
  })

  const [title, setTitle] = useState('Terms of Service')
  const [content, setContent] = useState('')
  const [seeded, setSeeded] = useState(false)

  // Seed the editor from the current version once (don't clobber edits on refetch).
  useEffect(() => {
    if (!seeded && data?.current) {
      setTitle(data.current.title || 'Terms of Service')
      setContent(data.current.content || '')
      setSeeded(true)
    } else if (!seeded && data && !data.current) {
      setSeeded(true) // no ToS yet — start with an empty editor
    }
  }, [data, seeded])

  const publish = useMutation({
    mutationFn: () =>
      apiRequest('/api/admin/tos', {
        method: 'POST',
        body: JSON.stringify({ content, title }),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin-tos'] }),
  })

  const onPublish = () => {
    if (!content.trim()) return
    const ok = window.confirm(
      'Publish a new version of the Terms of Service?\n\n' +
        'Every user will be required to re-accept the next time they open Mat-E.',
    )
    if (ok) publish.mutate()
  }

  if (isLoading) return <div className="p-6">Loading…</div>
  if (error) return <div className="p-6 text-error">Failed to load Terms of Service.</div>

  const current = data?.current
  const history = data?.history ?? []

  return (
    <div className="p-6 space-y-6 max-w-4xl">
      <div>
        <h1 className="page-title">Terms of Service</h1>
        <p className="text-base-content/60 text-sm mt-1">
          Edit and publish the Terms of Service users must accept before using Mat-E.
          Publishing a new version forces everyone to re-accept.
        </p>
      </div>

      <div className="alert alert-warning text-sm">
        <span>
          {current
            ? `Current live version: v${current.version}${
                current.published_at
                  ? ' · published ' + new Date(current.published_at).toLocaleString()
                  : ''
              }`
            : 'No Terms of Service published yet — users are not gated until you publish one.'}
        </span>
      </div>

      <div className="card bg-base-100 border border-base-300">
        <div className="card-body gap-4">
          <div>
            <label className="label"><span className="label-text font-medium">Title</span></label>
            <input
              className="input input-bordered w-full"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Terms of Service"
            />
          </div>
          <div>
            <label className="label">
              <span className="label-text font-medium">Content</span>
              <span className="label-text-alt text-base-content/50">Shown to users in the acceptance modal</span>
            </label>
            <textarea
              className="textarea textarea-bordered w-full font-mono text-sm min-h-[360px]"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Enter the Terms of Service text…"
            />
          </div>

          {publish.isError && (
            <p className="text-error text-sm">
              {(publish.error as Error)?.message || 'Publish failed.'}
            </p>
          )}
          {publish.isSuccess && (
            <p className="text-success text-sm">Published — users will re-accept on next load.</p>
          )}

          <div className="flex items-center justify-end gap-3">
            <button
              className="btn btn-primary"
              onClick={onPublish}
              disabled={publish.isPending || !content.trim()}
            >
              {publish.isPending ? 'Publishing…' : 'Publish new version'}
            </button>
          </div>
        </div>
      </div>

      {history.length > 0 && (
        <div className="card bg-base-100 border border-base-300">
          <div className="card-body">
            <h2 className="font-semibold mb-2">Version history</h2>
            <div className="overflow-x-auto">
              <table className="table table-sm">
                <thead>
                  <tr>
                    <th>Version</th>
                    <th>Published</th>
                    <th>By</th>
                    <th className="text-right">Acceptances</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((h) => (
                    <tr key={h.version}>
                      <td>
                        v{h.version}
                        {current && h.version === current.version && (
                          <span className="badge badge-success badge-sm ml-2">live</span>
                        )}
                      </td>
                      <td>{h.published_at ? new Date(h.published_at).toLocaleString() : '—'}</td>
                      <td className="text-base-content/70">{h.updated_by || '—'}</td>
                      <td className="text-right tabular-nums">{h.acceptances}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
